import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from './Style.module.css';
import AdminService from '../../API/AdminService';
import logger from '../../utils/logger';
import {
    IconArrowLeft, IconBook, IconSearch, IconChevronUp, IconChevronDown,
    IconClose, IconAlert, IconTrash,
} from '../../components/Icons';

// SVG-стрелки сортировки — заменяют unicode ↕↑↓
function SortIcon({ active, dir }) {
    if (!active) {
        return (
            <span style={{
                display: 'inline-flex', flexDirection: 'column', marginLeft: 4,
                color: 'var(--sk-content-text-muted)', opacity: 0.6,
            }}>
                <IconChevronUp width={9} height={9}/>
                <IconChevronDown width={9} height={9} style={{ marginTop: -2 }}/>
            </span>
        );
    }
    return (
        <span style={{ display: 'inline-flex', marginLeft: 4, color: '#b08620' }}>
            {dir === 'asc' ? <IconChevronUp width={12} height={12}/> : <IconChevronDown width={12} height={12}/>}
        </span>
    );
}

const EditCourse = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Состояние сортировки
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    
    // Модальные окна
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Дни недели для выпадающего списка
    const daysOfWeek = [
        { value: '', label: 'Не выбрано' },
        { value: 1, label: 'Понедельник' },
        { value: 2, label: 'Вторник' },
        { value: 3, label: 'Среда' },
        { value: 4, label: 'Четверг' },
        { value: 5, label: 'Пятница' },
        { value: 6, label: 'Суббота' },
        { value: 0, label: 'Воскресенье' }
    ];

    // Статусы для выпадающего списка
    const statusOptions = [
        { value: 'Активный', label: 'Активный' },
        { value: 'Завершённый', label: 'Завершённый' }
    ];
    
    const [editedCourse, setEditedCourse] = useState({
        name: '',
        day: '',
        timeStart: '',
        timeEnd: '',
        dateStart: '',
        dateEnd: '',
        status: ''
    });

    // Загрузка курсов при монтировании
    useEffect(() => {
        fetchCourses();
    }, []);

    // Фильтрация курсов при изменении поискового запроса
    useEffect(() => {
        filterCourses();
    }, [courses, searchTerm]);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const response = await AdminService.getAllCourses();
            setCourses(response.data || []);
        } catch (error) {
            logger.error('Ошибка загрузки курсов:', error);
            setError('Ошибка загрузки курсов');
        } finally {
            setLoading(false);
        }
    };

    // Функция фильтрации
    const filterCourses = () => {
        if (!searchTerm) {
            setFilteredCourses(courses);
            return;
        }

        const filtered = courses.filter(course => {
            const searchLower = searchTerm.toLowerCase();
            return (
                (course.name || '').toLowerCase().includes(searchLower) ||
                (course.status || '').toLowerCase().includes(searchLower) ||
                (course.timeStart || '').toLowerCase().includes(searchLower) ||
                (course.timeEnd || '').toLowerCase().includes(searchLower) ||
                (course.dateStart || '').toLowerCase().includes(searchLower) ||
                (course.dateEnd || '').toLowerCase().includes(searchLower) ||
                getDayName(course.day).toLowerCase().includes(searchLower)
            );
        });
        setFilteredCourses(filtered);
    };

    // Функция сортировки
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        const sortedCourses = [...filteredCourses].sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            // Особая обработка для дня недели
            if (key === 'day') {
                aValue = getDayName(aValue);
                bValue = getDayName(bValue);
            }

            // Особая обработка для времени
            if (key === 'time') {
                aValue = a.timeStart || '';
                bValue = b.timeStart || '';
            }

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (direction === 'asc') {
                return aValue.toString().localeCompare(bValue.toString(), 'ru', { numeric: true });
            } else {
                return bValue.toString().localeCompare(aValue.toString(), 'ru', { numeric: true });
            }
        });
        
        setFilteredCourses(sortedCourses);
    };

    // Иконка сортировки колонки — SVG-стрелки
    const getSortIcon = (columnKey) => (
        <SortIcon active={sortConfig.key === columnKey} dir={sortConfig.direction}/>
    );

    // Обработка клика по строке таблицы
    const handleRowClick = (course) => {
        setSelectedCourse(course);
        setEditedCourse({
            name: course.name || '',
            day: course.day ?? '',
            timeStart: course.timeStart || '',
            timeEnd: course.timeEnd || '',
            dateStart: course.dateStart || '',
            dateEnd: course.dateEnd || '',
            status: course.status || 'Активный'
        });
        setShowEditModal(true);
    };

    // Обработка изменения полей в модальном окне
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedCourse(prev => ({
            ...prev,
            [name]: name === 'day' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    // Сохранение изменений
    const handleSave = async () => {
        if (!editedCourse.name) {
            showNotification('Название курса обязательно для заполнения', 'error');
            return;
        }

        setIsSaving(true);
        try {
            await AdminService.updateCourse(selectedCourse.id, editedCourse);
            showNotification('Курс успешно обновлён!', 'success');
            setShowEditModal(false);
            fetchCourses(); // Перезагружаем список
        } catch (error) {
            logger.error('Ошибка обновления курса:', error);
            showNotification('Ошибка обновления курса', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Подтверждение удаления
    const handleDeleteClick = () => {
        setShowEditModal(false);
        setShowDeleteModal(true);
    };

    // Удаление курса
    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await AdminService.deleteCourse(selectedCourse.id);
            showNotification('Курс успешно удалён!', 'success');
            setShowDeleteModal(false);
            fetchCourses(); // Перезагружаем список
        } catch (error) {
            logger.error('Ошибка удаления курса:', error);
            showNotification('Ошибка удаления курса', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    // Показать уведомление
    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => {
            setNotification('');
        }, 3000);
    };

    // Получить название дня по числу
    const getDayName = (dayNumber) => {
        const day = daysOfWeek.find(d => d.value === dayNumber);
        return day ? day.label : (dayNumber !== null && dayNumber !== undefined && dayNumber !== '' ? dayNumber : 'Не выбрано');
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loading}>Загрузка курсов...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.error}>{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                <button onClick={() => navigate('/main/jornal/settings')} className={cl.backLink}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К настройкам</span>
                </button>

                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconBook width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Редактирование курсов</h1>
                                <span className={cl.pageHeaderPill}>
                                    <span className={cl.pageHeaderPillStrong}>{filteredCourses.length}</span>
                                    <span className={cl.pageHeaderPillMuted}>
                                        {filteredCourses.length === 1 ? 'курс' : 'курсов'}
                                    </span>
                                </span>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Настройки · Курсы
                            </div>
                        </div>
                    </div>
                </div>

                {/* Уведомления */}
                {notification && (
                    <div className={notification.type === 'success' ? cl.notification : cl.error}>
                        {notification.message}
                    </div>
                )}

                {/* Поле поиска */}
                <div className={cl.searchContainer}>
                    <span className={cl.searchIcon}>
                        <IconSearch width={18} height={18}/>
                    </span>
                    <input
                        type="text"
                        placeholder="Поиск по всем полям…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={cl.searchInput}
                    />
                </div>

                {/* Таблица курсов */}
                <div className={cl.tableContainer}>
                    {filteredCourses.length === 0 ? (
                        <div className={cl.noData}>
                            {searchTerm ? 'Курсы не найдены по запросу' : 'Курсы не найдены'}
                        </div>
                    ) : (
                        <table className={cl.studentsTable}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                        Название{getSortIcon('name')}
                                    </th>
                                    <th onClick={() => handleSort('day')} style={{ cursor: 'pointer' }}>
                                        День недели{getSortIcon('day')}
                                    </th>
                                    <th onClick={() => handleSort('time')} style={{ cursor: 'pointer' }}>
                                        Время{getSortIcon('time')}
                                    </th>
                                    <th onClick={() => handleSort('dateStart')} style={{ cursor: 'pointer' }}>
                                        Дата начала{getSortIcon('dateStart')}
                                    </th>
                                    <th onClick={() => handleSort('dateEnd')} style={{ cursor: 'pointer' }}>
                                        Дата окончания{getSortIcon('dateEnd')}
                                    </th>
                                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                        Статус{getSortIcon('status')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCourses.map((course) => (
                                    <tr
                                        key={course.id}
                                        className={cl.studentRow}
                                        onClick={() => handleRowClick(course)}
                                    >
                                        <td data-label="Название">
                                            <div className={cl.studentName}>
                                                {course.name}
                                            </div>
                                        </td>
                                        <td data-label="День">{getDayName(course.day)}</td>
                                        <td data-label="Время">
                                            {course.timeStart && course.timeEnd
                                                ? `${course.timeStart} - ${course.timeEnd}`
                                                : course.timeStart || course.timeEnd || '-'
                                            }
                                        </td>
                                        <td data-label="Начало">{course.dateStart || '-'}</td>
                                        <td data-label="Окончание">{course.dateEnd || '-'}</td>
                                        <td data-label="Статус">
                                            <span className={course.status === 'Завершённый' ? cl.statusArchive : cl.statusActive}>
                                                {course.status || 'Активный'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Модальное окно редактирования */}
            {showEditModal && selectedCourse && (
                <ModalForm
                    title="Редактировать курс"
                    icon={<IconBook width={18} height={18}/>}
                    onClose={() => setShowEditModal(false)}
                >
                    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className={cl.modalForm}>
                        <div className={cl.modalFormRow}>
                            <Field label="Название курса *">
                                <input
                                    type="text"
                                    name="name"
                                    value={editedCourse.name}
                                    onChange={handleInputChange}
                                    className={cl.modalInput}
                                    required
                                />
                            </Field>
                            <Field label="День недели">
                                <select
                                    name="day"
                                    value={editedCourse.day}
                                    onChange={handleInputChange}
                                    className={cl.modalInput}
                                >
                                    {daysOfWeek.map(day => (
                                        <option key={day.value} value={day.value}>
                                            {day.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Время начала">
                                <input type="time" name="timeStart" value={editedCourse.timeStart}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Время окончания">
                                <input type="time" name="timeEnd" value={editedCourse.timeEnd}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Дата начала курса">
                                <input type="date" name="dateStart" value={editedCourse.dateStart}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Дата окончания курса">
                                <input type="date" name="dateEnd" value={editedCourse.dateEnd}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <Field label="Статус курса">
                            <select
                                name="status"
                                value={editedCourse.status}
                                onChange={handleInputChange}
                                className={cl.modalInput}
                            >
                                {statusOptions.map(status => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <div className={cl.modalActionsSplit}>
                            <button
                                type="button"
                                onClick={handleDeleteClick}
                                className={cl.btnDanger}
                            >
                                <IconTrash width={14} height={14}/>
                                <span>Удалить курс</span>
                            </button>
                            <div className={cl.modalActionsRight}>
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className={cl.btnSecondary}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className={cl.btnPrimary}
                                >
                                    {isSaving ? 'Сохранение…' : 'Сохранить'}
                                </button>
                            </div>
                        </div>
                    </form>
                </ModalForm>
            )}

            {/* Модальное окно подтверждения удаления */}
            {showDeleteModal && (
                <ModalForm
                    title="Удалить курс?"
                    icon={<IconAlert width={18} height={18}/>}
                    onClose={() => setShowDeleteModal(false)}
                    variant="danger"
                >
                    <div className={cl.confirmBody}>
                        <p className={cl.confirmText}>
                            Вы точно хотите удалить курс <strong>«{selectedCourse?.name}»</strong>?
                        </p>
                        <p className={cl.confirmHint}>
                            Восстановить его будет нельзя — все данные, связанные с курсом, сотрутся.
                        </p>
                    </div>
                    <div className={cl.modalActions}>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className={cl.btnSecondary}
                        >
                            Отмена
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting}
                            className={cl.btnDangerSolid}
                        >
                            {isDeleting ? 'Удаление…' : 'Да, удалить'}
                        </button>
                    </div>
                </ModalForm>
            )}
        </div>
    );
};


// Универсальная модалка-форма (тёмный overlay, светлая карточка, иконка в заголовке)
function ModalForm({ title, icon, onClose, variant, children }) {
    const iconClass = variant === 'danger'
        ? `${cl.modalCardIcon} ${cl.modalCardIconDanger}`
        : cl.modalCardIcon;
    return (
        <div className={cl.modalOverlayV2} onClick={onClose}>
            <div className={cl.modalCardV2} onClick={e => e.stopPropagation()}>
                <header className={cl.modalCardHead}>
                    <div className={cl.modalCardTitleWrap}>
                        <span className={iconClass}>{icon}</span>
                        <h2 className={cl.modalCardTitle}>{title}</h2>
                    </div>
                    <button className={cl.modalCardClose} onClick={onClose} aria-label="Закрыть">
                        <IconClose width={18} height={18}/>
                    </button>
                </header>
                <div className={cl.modalCardBody}>
                    {children}
                </div>
            </div>
        </div>
    );
}

// Поле формы с лейблом
function Field({ label, children }) {
    return (
        <div className={cl.modalField}>
            <label className={cl.modalLabel}>{label}</label>
            {children}
        </div>
    );
}

export default EditCourse;